import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  RotateCcw, 
  Eye, 
  RefreshCw, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Search,
  X,
  Database,
  Mail,
  Settings,
  FileText,
  BarChart3,
  Layers,
  ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';
import Toast from '../components/ui/Toast';
import { flowsAdminApi } from '../services/api';
import { useSofteamboardStore } from '../store/softeamboardStore';
import { ConfirmDialog } from '../components/shared/ConfirmDialog';

// Updated Flow Detail Modal Component
const FlowDetailModal = ({ flow, open, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  if (!open || !flow) return null;

  const formatPayload = (payload) => {
    if (!payload) return 'No payload data';
    try {
      return JSON.stringify(payload, null, 2);
    } catch {
      return String(payload);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-labelledby="flow-detail-title">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 id="flow-detail-title" className="text-xl font-semibold">Flow Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Basic Information</h3>
              <div className="space-y-2 text-sm">
                <div><span className="font-medium">Flow ID:</span> {flow.flowId}</div>
                <div><span className="font-medium">Type:</span> {flow.type}</div>
                <div><span className="font-medium">Status:</span> {flow.status}</div>
                <div><span className="font-medium">Progress:</span> {flow.progress}%</div>
                <div><span className="font-medium">Category:</span> {flow.category}</div>
              </div>
            </div>
            
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Timing</h3>
              <div className="space-y-2 text-sm">
                <div><span className="font-medium">Started:</span> {flow.startedAt ? new Date(flow.startedAt).toLocaleString() : '-'}</div>
                <div><span className="font-medium">Completed:</span> {flow.completedAt ? new Date(flow.completedAt).toLocaleString() : '-'}</div>
                <div><span className="font-medium">Duration:</span> {flow.duration || '-'}</div>
              </div>
            </div>
          </div>

          {flow.needId && (
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-2">Related Need</h3>
              <div className="text-sm">
                <div><span className="font-medium">Need ID:</span> {flow.needId}</div>
                {flow.needName && <div><span className="font-medium">Need Name:</span> {flow.needName}</div>}
              </div>
            </div>
          )}

          {flow.message && (
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-2">Message</h3>
              <div className="bg-gray-50 p-3 rounded text-sm">
                {flow.message}
              </div>
            </div>
          )}

          {flow.error && (
            <div className="mb-6">
              <h3 className="font-medium text-red-900 mb-2">Error Details</h3>
              <div className="bg-red-50 p-3 rounded text-sm text-red-800">
                {flow.error}
              </div>
            </div>
          )}

          {/* Screenshot Section */}
          {flow.metadata?.screenshotUrl && (
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-2">Screenshot</h3>
              <div className="bg-gray-50 p-4 rounded">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Screenshot captured during flow execution
                  </div>
                  <button
                    onClick={() => window.open(flow.metadata.screenshotUrl, '_blank')}
                    className="inline-flex items-center px-3 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <ExternalLink size={16} className="mr-2" />
                    View Screenshot
                  </button>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Click to open in new tab for viewing or downloading
                </div>
              </div>
            </div>
          )}

          <div>
            <h3 className="font-medium text-gray-900 mb-2">Original Payload</h3>
            <pre className="bg-gray-50 p-4 rounded text-xs overflow-x-auto">
              {formatPayload(flow.originalPayload || flow.payload)}
            </pre>
          </div>

          {flow.metadata && (
            <div className="mt-6">
              <h3 className="font-medium text-gray-900 mb-2">Metadata</h3>
              <pre className="bg-gray-50 p-4 rounded text-xs overflow-x-auto">
                {formatPayload(flow.metadata)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const FlowsAdmin = () => {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [toasts, setToasts] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [confirmCancel, setConfirmCancel] = useState({ open: false, flowId: null });

  // Add refs to track ongoing requests and intervals
  const fetchingRef = useRef(false);
  const intervalRef = useRef(null);
  const refreshTimeoutRef = useRef(null);

  // Tab configuration
  const tabs = [
    {
      id: 'all',
      name: 'All',
      icon: Layers,
      description: 'All flows across categories',
      categories: []
    },
    {
      id: 'mail',
      name: 'Mail',
      icon: Mail,
      description: 'Email processing and generation flows',
      categories: ['mail_processing']
    },
    {
      id: 'analysis',
      name: 'Analysis',
      icon: BarChart3,
      description: 'File analysis, scoring, and entity updates',
      categories: ['analysis_processing']
    },
    {
      id: 'crm_rpa',
      name: 'CRM-RPA',
      icon: Settings,
      description: 'Salesforce automation and RPA flows',
      categories: ['crm_rpa']
    }
  ];

  // Debounced fetch function to prevent multiple simultaneous calls
  const fetchFlows = useCallback(async (force = false) => {
    // Prevent concurrent requests unless forced
    if (fetchingRef.current && !force) {
      console.log('⏩ Skipping fetch - already in progress');
      return;
    }

    // Don't auto-refresh too frequently (minimum 30 seconds between auto-refreshes)
    if (!force && Date.now() - lastRefresh < 30000) {
      console.log('⏩ Skipping fetch - too soon since last refresh');
      return;
    }

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('🔄 Fetching flows...');
      const response = await flowsAdminApi.listFlows();
      setFlows(response || []);
      setLastRefresh(Date.now());
      console.log(`✅ Fetched ${response?.length || 0} flows`);
    } catch (err) {
      console.error('❌ Error fetching flows:', err);
      setError(err.message || 'Failed to fetch flows');
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [lastRefresh]);

  // Initial load
  useEffect(() => {
    fetchFlows(true); // Force initial load
  }, []);

  // Manual refresh function
  const handleManualRefresh = useCallback(() => {
    console.log('🔄 Manual refresh triggered');
    fetchFlows(true);
  }, [fetchFlows]);

  // Auto-refresh every 2 minutes (instead of 30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchFlows(false); // Don't force, respect rate limiting
    }, 120000); // 2 minutes

    intervalRef.current = interval;
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchFlows]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // Schedule a delayed refresh to avoid excessive API calls
  const scheduleRefresh = useCallback((delay = 1000) => {
    // Clear any existing scheduled refresh
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    
    // Schedule new refresh
    refreshTimeoutRef.current = setTimeout(() => {
      fetchFlows(false); // Don't force, respect rate limiting
    }, delay);
  }, [fetchFlows]);

  const showToast = (message, type = 'info') => {
    const id = Date.now();
    const toast = { id, message, type };
    setToasts(prev => [...prev, toast]);
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const handleFlowAction = async (flowId, action, actionName) => {
    try {
      setActionLoading(prev => ({ ...prev, [`${flowId}-${action}`]: true }));
      
      // Use the appropriate flowsAdminApi method
      switch (action) {
        case 'retry-poll':
          await flowsAdminApi.retryPoll(flowId);
          break;
        case 'retry-classify':
          await flowsAdminApi.retryClassify(flowId);
          break;
        case 'retry-apply':
          await flowsAdminApi.retryApply(flowId);
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }
      
      showToast(`${actionName} retriggered successfully`, 'success');
      
      // Schedule refresh instead of immediate call
      scheduleRefresh(1000);
      
    } catch (err) {
      showToast(`Failed to ${actionName.toLowerCase()}: ${err.message}`, 'error');
      console.error(`Error with ${action}:`, err);
    } finally {
      setActionLoading(prev => ({ ...prev, [`${flowId}-${action}`]: false }));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      processing: { color: 'bg-blue-100 text-blue-800', icon: Clock },
      error: { color: 'bg-red-100 text-red-800', icon: AlertCircle },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      cancelled: { color: 'bg-gray-100 text-gray-800', icon: X },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        <Icon size={12} className="mr-1" />
        {status}
      </span>
    );
  };

  const getTypeIcon = (type) => {
    const typeIcons = {
      mail_generation: Mail,
      need_description_mail: Mail,
      relevance_scoring: BarChart3,
      entity_update: Database,
      mail_processing: Mail,
      file_analysis: FileText,
      generic: Settings,
    };
    
    const Icon = typeIcons[type] || typeIcons.generic;
    return <Icon size={16} className="text-gray-500" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const formatDuration = (duration) => {
    if (!duration) return '-';
    
    // Handle different duration formats
    if (typeof duration === 'string') {
      // If it's already formatted (e.g., "2m 30s"), return as is
      if (duration.includes('m') || duration.includes('s') || duration.includes('h')) {
        return duration;
      }
      
      // Try to parse as number
      const parsed = parseFloat(duration);
      if (isNaN(parsed)) return duration;
      duration = parsed;
    }
    
    if (typeof duration === 'number') {
      // Assume duration is in seconds
      const hours = Math.floor(duration / 3600);
      const minutes = Math.floor((duration % 3600) / 60);
      const seconds = Math.floor(duration % 60);
      
      if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s`;
      } else if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
      } else {
        return `${seconds}s`;
      }
    }
    
    return String(duration);
  };

  // Filter flows based on active tab
  const getFilteredFlows = () => {
    let filtered = flows;
    
    // Filter by tab category
    const activeTabConfig = tabs.find(tab => tab.id === activeTab);
    if (activeTabConfig && activeTabConfig.categories.length > 0) {
      filtered = filtered.filter(flow => 
        activeTabConfig.categories.includes(flow.category)
      );
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(flow =>
        flow.flowId?.toLowerCase().includes(term) ||
        flow.type?.toLowerCase().includes(term) ||
        flow.needId?.toLowerCase().includes(term) ||
        flow.candidateEmail?.toLowerCase().includes(term) ||
        flow.clientEmail?.toLowerCase().includes(term)
      );
    }
    
    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(flow => flow.status === statusFilter);
    }
    
    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(flow => flow.type === typeFilter);
    }
    
    return filtered;
  };

  const getTabStats = (tabId) => {
    const tabConfig = tabs.find(tab => tab.id === tabId);
    if (!tabConfig) return { total: 0, active: 0 };
    
    let tabFlows = flows;
    if (tabConfig.categories.length > 0) {
      tabFlows = flows.filter(flow => 
        tabConfig.categories.includes(flow.category)
      );
    }
    
    return {
      total: tabFlows.length,
      active: tabFlows.filter(flow => 
        flow.status === 'processing' || flow.status === 'pending'
      ).length
    };
  };

  // Updated general retry logic
  const handleRetryFlow = async (flow) => {
    const endpoint = getRetryEndpoint(flow);
    const actionName = endpoint.replace('retry-', '').replace('-', ' ');
    await handleFlowAction(flow.flowId, endpoint, `Retry ${actionName}`);
  };

  // Updated retry endpoint mapping
  const getRetryEndpoint = (flow) => {
    // Map flow types to their retry endpoints
    const retryMap = {
      'mail_processing': 'retry-poll',
      'need_description_mail': 'retry-poll',
      'mail_generation': 'retry-poll',
      'relevance_scoring': 'retry-classify',
      'file_analysis': 'retry-classify',
      'entity_update': 'retry-apply',
    };
    
    return retryMap[flow.type] || 'retry-poll';
  };

  const handleViewFlow = (flow) => {
    setSelectedFlow(flow);
    setShowDetailModal(true);
  };

  const canRetry = (flow) => {
    return flow.status === 'error' || flow.status === 'failed';
  };

  const canCancel = (flow) => {
    // Can cancel flows that are processing or pending, but not completed/error/cancelled
    return ['processing', 'pending'].includes(flow.status);
  };

  const handleCancelFlow = (flowId) => {
    setConfirmCancel({ open: true, flowId });
  };

  const executeCancelFlow = async (flowId) => {
    try {
      setActionLoading(prev => ({ ...prev, [`${flowId}-cancel`]: true }));

      await flowsAdminApi.cancelFlow(flowId);

      showToast('Flow cancelled successfully', 'success');

      scheduleRefresh(1000);
      
    } catch (err) {
      showToast(`Failed to cancel flow: ${err.message}`, 'error');
      console.error('Error cancelling flow:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [`${flowId}-cancel`]: false }));
      setConfirmCancel({ open: false, flowId: null });
    }
  };

  const filteredFlows = getFilteredFlows();
  
  // Get unique statuses and types for filters
  const uniqueStatuses = [...new Set(flows.map(flow => flow.status))];
  const uniqueTypes = [...new Set(flows.map(flow => flow.type))];

  // Get current tab stats
  const currentTabStats = {
    total: filteredFlows.length,
    processing: filteredFlows.filter(flow => flow.status === 'processing').length,
    completed: filteredFlows.filter(flow => flow.status === 'completed').length,
    error: filteredFlows.filter(flow => flow.status === 'error').length,
  };

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-center">
            <AlertCircle className="text-red-500 mr-2" size={20} />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error loading flows</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
          <button
            onClick={() => fetchFlows(true)}
            className="mt-3 text-sm bg-red-100 text-red-800 px-3 py-1 rounded hover:bg-red-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Flow Administration</h1>
              <p className="text-gray-600 mt-1">Monitor and manage automated flows</p>
            </div>
            
            {/* Toolbar with RPA button */}
            <div className="flex gap-3">
              <Link
                to="/admin/workflows"
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
              >
                Prefer /admin/workflows
              </Link>

              <button
                onClick={handleManualRefresh}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {tabs.map((tab) => {
                  const stats = getTabStats(tab.id);
                  const Icon = tab.icon;
                  
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        flex items-center py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                        ${activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }
                      `}
                    >
                      <Icon size={16} className="mr-2" />
                      {tab.name}
                      {stats.total > 0 && (
                        <span className={`
                          ml-2 px-2 py-0.5 text-xs rounded-full
                          ${activeTab === tab.id
                            ? 'bg-blue-100 text-blue-600'
                            : 'bg-gray-100 text-gray-600'
                          }
                        `}>
                          {stats.active > 0 ? `${stats.active}/${stats.total}` : stats.total}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Controls */}
          <div className="mb-6 flex flex-col lg:flex-row gap-4 justify-between">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search flows..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label="Search flows"
                />
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Filter by status"
              >
                <option value="all">All Statuses</option>
                {uniqueStatuses.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Filter by type"
              >
                <option value="all">All Types</option>
                {uniqueTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Stats for active tab */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center">
                <Layers className="text-blue-500 mr-2" size={20} />
                <div>
                  <p className="text-sm text-gray-600">Total Flows</p>
                  <p className="text-xl font-semibold">{currentTabStats.total}</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center">
                <Clock className="text-yellow-500 mr-2" size={20} />
                <div>
                  <p className="text-sm text-gray-600">Processing</p>
                  <p className="text-xl font-semibold">{currentTabStats.processing}</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center">
                <CheckCircle className="text-green-500 mr-2" size={20} />
                <div>
                  <p className="text-sm text-gray-600">Completed</p>
                  <p className="text-xl font-semibold">{currentTabStats.completed}</p>
                </div>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center">
                <AlertCircle className="text-red-500 mr-2" size={20} />
                <div>
                  <p className="text-sm text-gray-600">Errors</p>
                  <p className="text-xl font-semibold">{currentTabStats.error}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Flows Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-gray-400" />
                <p className="text-gray-500">Loading flows...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Flow
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Progress
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Need
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Candidate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Started
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredFlows.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                          {searchTerm ? 'No flows match your search criteria' : 'No flows found'}
                        </td>
                      </tr>
                    ) : (
                      filteredFlows.map((flow) => (
                        <tr key={flow.flowId || flow._flowId} className="hover:bg-gray-50">
                          {/* Flow Info */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {getTypeIcon(flow.type)}
                              <div className="ml-3">
                                <div className="text-sm font-medium text-gray-900">
                                  {flow.flowId || flow._flowId}
                                </div>
                                <div className="text-sm text-gray-500">{flow.type}</div>
                              </div>
                            </div>
                          </td>
                          
                          {/* Status */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(flow.status)}
                          </td>
                          
                          {/* Progress */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            {flow.progress !== undefined && flow.progress >= 0 ? (
                              <div className="flex items-center">
                                <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2 w-16">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${flow.progress}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-600">{flow.progress}%</span>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          
                          {/* Need */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {flow.needId ? (
                              <div>
                                <div className="font-mono text-xs">{flow.needId.slice(0, 8)}...</div>
                                {flow.needName && (
                                  <div className="text-xs text-gray-500 truncate max-w-32" title={flow.needName}>
                                    {flow.needName}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          
                          {/* Candidate */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {flow.candidateEmail || flow.candidateName ? (
                              <div>
                                {flow.candidateName && (
                                  <div className="text-xs font-medium">{flow.candidateName}</div>
                                )}
                                {flow.candidateEmail && (
                                  <div className="text-xs text-gray-500">{flow.candidateEmail}</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          
                          {/* Started */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(flow.startedAt)}
                          </td>
                          
                          {/* Duration */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDuration(flow.duration)}
                          </td>
                          
                          {/* Actions */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              {/* Detail Button - Always available */}
                              <button
                                onClick={() => handleViewFlow(flow)}
                                className="text-blue-600 hover:text-blue-900"
                                title="View flow details"
                                aria-label="View flow details"
                              >
                                <Eye size={16} />
                              </button>
                              
                              {/* Retry Button - For failed flows */}
                              {canRetry(flow) && (
                                <button
                                  onClick={() => handleRetryFlow(flow)}
                                  disabled={actionLoading[`${flow.flowId}-retry`]}
                                  className="text-green-600 hover:text-green-900 disabled:opacity-50"
                                  title={flow.category === 'crm_rpa' ? 'Restart RPA flow' : 'Retry flow'}
                                  aria-label={flow.category === 'crm_rpa' ? 'Restart RPA flow' : 'Retry flow'}
                                >
                                  {actionLoading[`${flow.flowId}-retry`] ? (
                                    <RefreshCw size={16} className="animate-spin" />
                                  ) : (
                                    <RotateCcw size={16} />
                                  )}
                                </button>
                              )}
                              
                              {/* External Link Button - For RPA flows with screenshots */}
                              {flow.category === 'crm_rpa' && flow.metadata?.screenshotUrl && (
                                <button
                                  onClick={() => window.open(flow.metadata.screenshotUrl, '_blank')}
                                  className="text-purple-600 hover:text-purple-900"
                                  title="View screenshot"
                                  aria-label="View screenshot"
                                >
                                  <ExternalLink size={16} />
                                </button>
                              )}
                              
                              {/* Cancel Button - For active flows */}
                              {canCancel(flow) && (
                                <button
                                  onClick={() => handleCancelFlow(flow.flowId)}
                                  disabled={actionLoading[`${flow.flowId}-cancel`]}
                                  className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                  title="Cancel flow"
                                  aria-label="Cancel flow"
                                >
                                  {actionLoading[`${flow.flowId}-cancel`] ? (
                                    <RefreshCw size={16} className="animate-spin" />
                                  ) : (
                                    <X size={16} />
                                  )}
                                </button>
                              )}
                              
                              {/* Status indicator for completed flows */}
                              {!canRetry(flow) && !canCancel(flow) && flow.status === 'completed' && (
                                <span className="text-green-600 text-xs flex items-center">
                                  <CheckCircle size={12} className="mr-1" />
                                  Done
                                </span>
                              )}
                              
                              {/* Status indicator for cancelled flows */}
                              {flow.status === 'cancelled' && (
                                <span className="text-gray-600 text-xs flex items-center">
                                  <X size={12} className="mr-1" />
                                  Cancelled
                                </span>
                              )}

                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Flow Detail Modal */}
          <FlowDetailModal
            flow={selectedFlow}
            open={showDetailModal}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedFlow(null);
            }}
          />

          {/* Toasts */}
          <div className="fixed bottom-4 right-4 z-50 space-y-2">
            {toasts.map((toast) => (
              <Toast
                key={toast.id}
                message={toast.message}
                type={toast.type}
                onClose={() => removeToast(toast.id)}
              />
            ))}
          </div>
        </div>
      </main>
      <ConfirmDialog
        open={confirmCancel.open}
        title="Cancel Flow"
        message="Are you sure you want to cancel this flow?"
        confirmLabel="Cancel Flow"
        variant="warning"
        onConfirm={() => executeCancelFlow(confirmCancel.flowId)}
        onCancel={() => setConfirmCancel({ open: false, flowId: null })}
      />
    </div>
  );
};

export default FlowsAdmin;